import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@primer/react";
import DataGrid, { Column, FilterRow, Grouping, HeaderFilter, Paging, Selection, Sorting } from "devextreme-react/data-grid";
import { Button } from "../../../components/buttons";
import { AnimationPopup } from "../../../components/modal";
import { ganMienGiamApi } from "../../../api/ganMienGiamApi";
import { showNotify } from "../../../helpers/toast";
import { NamHocTruongKhoiHeFilter } from "../../../models/namHocTruongKhoiHeFilter";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/reducers";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { ComboboxTruong } from "../../../components/combobox-truong";
import ComboboxKhoi from "../../../components/combobox-khoi";
import { ComboboxHe } from "../../../components/combobox-he";
import ModalActions from "../../../components/ui/modal/ModalActions";

type GanMienGiamAddNewModalProps = {
    onClose: () => void;
    onSuccess: () => void;
    animationOf: string;
    filter: NamHocTruongKhoiHeFilter;
    nam_hoc: string;
}

export const GanMienGiamAddNewModal = (props: GanMienGiamAddNewModalProps) => {
    const gridRef = useRef<any>();
    const hinhThucGridRef = useRef<any>();
    const [selectedHinhThucIds, setSelectedHinhThucIds] = useState<number[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [tsHocSinhs, setTsHocSinhs] = useState<any[]>([]);
    const [selectedCount, setSelectedCount] = useState(0);
    const [selectedHinhThucCount, setSelectedHinhThucCount] = useState(0);
    const [filterTruongId, setFilterTruongId] = useState<number>(props.filter.dm_truong_id || 0);
    const [filterKhoiId, setFilterKhoiId] = useState<number>(props.filter.dm_khoi_id || 0);
    const [filterHeId, setFilterHeId] = useState<number>(props.filter.dm_he_id || 0);
    const state = useSelector((x: RootState) => x.ganMienGiam);
    const {sf_hinhthucmiengiams } = state;
    const handleLoadHocSinh = useCallback(async () => {
        const res = await hocSinhApi.selectShortInformation({
            nam_hoc: props.nam_hoc,
            dm_truong_id:  0,
            dm_he_id: 0,
            dm_khoi_id: 0
        });
        if (res.is_success) {
            setTsHocSinhs(res.data);
        }
    }, [props.filter.nam_hoc]);

    useEffect(() => {
        handleLoadHocSinh();
    }, []);

    // Filter học sinh theo các combo box
    const filteredStudents = useMemo(() => {
        if (!tsHocSinhs || tsHocSinhs.length === 0) {
            return [];
        }
        return tsHocSinhs.filter((hs: any) => {
            if (filterTruongId && filterTruongId > 0 && hs.dm_truong_id !== filterTruongId) return false;
            if (filterKhoiId && filterKhoiId > 0 && hs.dm_khoi_id !== filterKhoiId) return false;
            if (filterHeId && filterHeId > 0 && hs.dm_he_id !== filterHeId) return false;
            return true;
        });
    }, [tsHocSinhs, filterTruongId, filterKhoiId, filterHeId]);


    const onClose = useCallback(() => {
        window.animationHidePopup(props.animationOf || "");
        setTimeout(() => {
            props.onClose();
        }, 500);
    }, [props.animationOf, props.onClose]);

    const handleSave = async () => {
        const selectedRows = gridRef.current?.instance.getSelectedRowsData() || [];
        const selectedStudentIds = selectedRows.map((row: any) => row.id) || [];
        
        if (selectedStudentIds.length === 0) {
            showNotify({ message: "Vui lòng chọn ít nhất một học sinh", type: "warning" });
            return;
        }
        if (selectedHinhThucIds.length === 0) {
            showNotify({ message: "Vui lòng chọn ít nhất một hình thức miễn giảm", type: "warning" });
            return;
        }

        setIsSaving(true);
        try {
            const result = await ganMienGiamApi.batchInsert({
                ts_hocsinh_ids: selectedStudentIds,
                sf_hinhthucmiengiam_ids: selectedHinhThucIds,
                nam_hoc: props.nam_hoc,
                so_lan_ap_dung: 1
            });
            
            if (result.is_success) {
                showNotify({ message: "Thêm miễn giảm thành công", type: "success" });
                props.onSuccess();
            } else {
                showNotify({ message: "Có lỗi xảy ra khi thêm miễn giảm", type: "error" });
            }
        } catch (error) {
            showNotify({ message: "Có lỗi xảy ra", type: "error" });
        } finally {
            setIsSaving(false);
        }
    };

    const columns = useMemo(() => [
        <Column key="ma_hs" dataField="ma_hs" caption="Mã HS" width={120} fixed={true} />,
        <Column key="trang_thai" dataField="trang_thai" caption="Trạng thái" width={120} />,

        <Column key="ho_ten" dataField="ho_ten" caption="Họ tên"/>,
        <Column key="ten_lop" dataField="ten_lop" caption="Lớp" width={80} />,
    ], []);

    return (
        <AnimationPopup
            title="Thêm mới miễn giảm"
            animationOf={props.animationOf}
            onClose={onClose}
            width="90%"
        >
            <Box sx={{ display: 'flex', gap: 2, minHeight: '500px' }}>
                <Box sx={{ flex: 1, border: '1px solid', borderColor: 'border.default', borderRadius: 2, p: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {selectedCount > 0 && (
                            <Box sx={{ fontSize: '12px', color: 'success.fg', fontWeight: 'bold' }}>
                                Đã chọn: {selectedCount} học sinh
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <ComboboxTruong
                                value={filterTruongId}
                                onValueChanged={(value) => {
                                    setFilterTruongId(value || 0);
                                    setFilterKhoiId(0);
                                    setFilterHeId(0);
                                }}
                                isShowClearButton={true}
                            />
                        </Box>
                        <Box>
                            <ComboboxKhoi
                                dm_truong_id={filterTruongId}
                                value={filterKhoiId}
                                width={150}
                                onValueChanged={(value) => {
                                    setFilterKhoiId(value || 0);
                                    setFilterHeId(0);
                                }}
                            />
                        </Box>  
                        <Box>
                            <ComboboxHe
                                width={150}
                                dm_khoi_id={filterKhoiId}
                                dm_truong_id={filterTruongId}
                                value={filterHeId}
                                onValueChanged={(value) => {
                                    setFilterHeId(value || 0);
                                }}
                            />
                        </Box>
                    </Box>
                    {filteredStudents.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'fg.muted' }}>
                            Không có học sinh nào
                        </Box>
                    ) : (
                        <DataGrid
                            ref={gridRef}
                            dataSource={filteredStudents}
                            keyExpr="id"
                            height={500}
                            showBorders={true}
                            columnAutoWidth={false}
                            noDataText="Không có dữ liệu"
                            showRowLines={true}
                            showColumnLines={true}
                            wordWrapEnabled={true}
                            rowAlternationEnabled={true}
                            hoverStateEnabled={true}
                            onSelectionChanged={() => {
                                if (gridRef.current) {
                                    const selectedRows = gridRef.current.instance.getSelectedRowsData();
                                    setSelectedCount(selectedRows.length);
                                }
                            }}
                        >
                            <Selection mode="multiple" showCheckBoxesMode="always" />
                            <FilterRow visible={true} />
                            <HeaderFilter visible={true} />
                            <Sorting mode="multiple" />
                            <Paging enabled={false} />
                            {columns}
                        </DataGrid>
                    )}
                </Box>
                <Box sx={{ flex: 1, border: '1px solid', borderColor: 'border.default', borderRadius: 2, p: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ fontWeight: 'bold' }}>Chọn hình thức miễn giảm</Box>
                        {selectedHinhThucCount > 0 && (
                            <Box sx={{ fontSize: '12px', color: 'success.fg', fontWeight: 'bold' }}>
                                Đã chọn: {selectedHinhThucCount} hình thức
                            </Box>
                        )}
                    </Box>
                    {!sf_hinhthucmiengiams || sf_hinhthucmiengiams.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'fg.muted' }}>
                            Không có hình thức miễn giảm nào
                        </Box>
                    ) : (
                        <DataGrid
                            ref={hinhThucGridRef}
                            dataSource={sf_hinhthucmiengiams}
                            keyExpr="id"
                            height={500}
                            showBorders={true}
                            columnAutoWidth={false}
                            noDataText="Không có dữ liệu"
                            showRowLines={true}
                            showColumnLines={true}
                            wordWrapEnabled={true}
                            rowAlternationEnabled={true}
                            hoverStateEnabled={true}
                            onRowPrepared={(e: any) => {
                                // Ẩn checkbox cho group row
                                if (e.rowType === 'group') {
                                    const checkboxCell = e.rowElement?.querySelector('.dx-select-checkbox');
                                    if (checkboxCell && checkboxCell instanceof HTMLElement) {
                                        checkboxCell.style.display = 'none';
                                    }
                                }
                            }}
                            onSelectionChanged={(e: any) => {
                                if (hinhThucGridRef.current) {
                                    const instance = hinhThucGridRef.current.instance;
                                    const selectedRows = instance.getSelectedRowsData();
                                    
                                    // Lọc bỏ group rows nếu có
                                    const hinhThucRows = selectedRows.filter((row: any) => row && row.id !== undefined);
                                    
                                    // Đảm bảo mỗi nhóm chỉ có 1 phần tử được chọn
                                    const nhomMap = new Map<number, any>();
                                    const rowsToDeselect: any[] = [];
                                    
                                    hinhThucRows.forEach((row: any) => {
                                        const nhomId = row.nhom_mien_giam;
                                        if (nhomMap.has(nhomId)) {
                                            // Nếu nhóm này đã có phần tử được chọn, bỏ chọn phần tử cũ
                                            rowsToDeselect.push(nhomMap.get(nhomId));
                                            nhomMap.set(nhomId, row);
                                        } else {
                                            nhomMap.set(nhomId, row);
                                        }
                                    });
                                    
                                    // Bỏ chọn các phần tử cũ trong cùng nhóm
                                    if (rowsToDeselect.length > 0) {
                                        rowsToDeselect.forEach((row: any) => {
                                            instance.deselectRows([row.id]);
                                        });
                                        // Lấy lại danh sách đã chọn sau khi bỏ chọn
                                        const finalSelectedRows = instance.getSelectedRowsData();
                                        const finalHinhThucRows = finalSelectedRows.filter((row: any) => row && row.id !== undefined);
                                        const hinhThucIds = finalHinhThucRows.map((row: any) => row.id).filter((id: any) => id && !isNaN(id));
                                        setSelectedHinhThucIds(hinhThucIds);
                                        setSelectedHinhThucCount(finalHinhThucRows.length);
                                    } else {
                                        const hinhThucIds = hinhThucRows.map((row: any) => row.id).filter((id: any) => id && !isNaN(id));
                                        setSelectedHinhThucIds(hinhThucIds);
                                        setSelectedHinhThucCount(hinhThucRows.length);
                                    }
                                }
                            }}
                        >
                            <Selection mode="multiple" showCheckBoxesMode="always" />
                            <Grouping autoExpandAll={true} />
                            {/* <GroupPanel visible={true} /> */}
                            <FilterRow visible={true} />
                            <HeaderFilter visible={true} />
                            <Sorting mode="multiple" />
                            <Paging enabled={false}/>
                            <Column 
                                dataField="nhom_mien_giam" 
                                caption="Nhóm" 
                                width={100}
                                groupIndex={0}
                                groupCellRender={(data: any) => {
                                    return `Nhóm ${data.value}`;
                                }}
                            />
                            <Column 
                                dataField="ma_hinh_thuc" 
                                caption="Mã" 
                                width={120}
                            />
                            <Column 
                                dataField="hinh_thuc_mien_giam" 
                                caption="Hình thức miễn giảm" 
                                // width={300}
                            />
                        </DataGrid>
                    )}
                </Box>
            </Box>

            <ModalActions>
                <Button type="normal" icon="fa fa-times" text="Đóng" onClick={onClose} />
                <Button
                    type="normal"
                    icon="fas fa-check-square"
                    text="Lưu"
                    onClick={handleSave}
                    isLoading={isSaving}
                />
            </ModalActions>
        </AnimationPopup>
    );
};
